/**
 * Unit-testable JSONB array containment SQL for Remult `$some` / `$none` filters.
 * Exact enum membership only (e.g. association.roles) - not substring LIKE.
 */

export type JsonbContainmentParamText = (value: string) => string;

/**
 * Builds `col @> jsonb_build_array(...)` (and optional NOT @>) for `$some` shapes.
 * Returns undefined when the filter must stay on the jsonb_array_elements slow path.
 */
export function buildJsonbArraySomeContainmentSql(
  colExpr: string,
  value: unknown,
  paramText: JsonbContainmentParamText,
): string | undefined {
  const buildJsonbBuildArray = (vals: string[]) =>
    `jsonb_build_array(${vals.map((x) => paramText(x)).join(", ")})`;

  // `$some: "VOLUNTEER"` → exact membership
  if (typeof value === "string") {
    return `${colExpr} @> jsonb_build_array(${paramText(value)})`;
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) return;

  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return;

  // Single-key shapes
  if (entries.length === 1) {
    const [k, v] = entries[0];

    if (k === "$contains" || k === "$in" || k === "=") {
      if (typeof v === "string") {
        return `${colExpr} @> jsonb_build_array(${paramText(v)})`;
      }
      if (
        k === "$contains" &&
        Array.isArray(v) &&
        v.every((x) => typeof x === "string")
      ) {
        return `${colExpr} @> ${buildJsonbBuildArray(v as string[])}`;
      }
      if (
        k === "$in" &&
        Array.isArray(v) &&
        v.every((x) => typeof x === "string")
      ) {
        const parts = (v as string[]).map(
          (x) => `${colExpr} @> jsonb_build_array(${paramText(x)})`,
        );
        if (parts.length === 0) return "1 = 0 /* $in with no values */";
        if (parts.length === 1) return parts[0];
        return `(${parts.join(" OR ")})`;
      }
      return;
    }

    return;
  }

  // Dual exact membership: `$some: { $contains: A, $notContains: B }`
  // Equivalent to: col @> [A] AND NOT (col @> [B])
  if (entries.length === 2) {
    const contains = entries.find(([k]) => k === "$contains");
    const notContains = entries.find(([k]) => k === "$notContains");
    if (
      contains &&
      notContains &&
      typeof contains[1] === "string" &&
      typeof notContains[1] === "string"
    ) {
      return `(${colExpr} @> jsonb_build_array(${paramText(contains[1])}) AND NOT (${colExpr} @> jsonb_build_array(${paramText(notContains[1])})))`;
    }
  }

  return;
}

/**
 * Builds NOT-containment for `$none` when the value is an exact string or
 * `{ $contains: "ROLE" }` / `{ $in: [...] }` (OR of containments, then negated).
 */
export function buildJsonbArrayNoneContainmentSql(
  colExpr: string,
  value: unknown,
  paramText: JsonbContainmentParamText,
): string | undefined {
  const some = buildJsonbArraySomeContainmentSql(colExpr, value, paramText);
  if (!some) return;
  return `NOT (${some})`;
}
